import s from '../solution-rate-reviews.scss';
import { config } from '../../../../../config/config';
import React, { useEffect } from 'react';
import { SortDropDown } from '..';
import { ModalNames } from '../../../../modals/modals';
import { SolutionPageCtx } from '../../..';
import { Origin } from '../../../../..';
import { composer } from '../../../../../component-decorators/composer';
import { biLogger } from '../../../../../bi';
import { TextButton, Text } from '@wix/design-system';
import type { TFunction } from 'i18next';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { BlockReviewsHelper } from './block-reviews-helper';
import type { WebSolution } from '@wix/ambassador-marketplace/types';
import type { ISolutionReview } from '../../../../../types/common/marketplace';
import type { MarketplaceStore } from '../../../../../component-decorators/with-marketplace-store';
import { useConfigContext } from '../../../../../config-context';
import { appMarketReviewAppNotInstalledAlert } from '@wix/bi-logger-market/v2';

interface IReviewsSectionHeaderProps
  extends Partial<InjectedExperimentsProps>,
    Partial<WithTranslationProps> {
  marketplaceStore?: MarketplaceStore;
  reviews: number;
  sortBy: any;
  afterPostRender: any;
  editMode?: { userReview: ISolutionReview; shouldDisplay: boolean };
  showDeletedLink?: boolean;
  appId: string;
}

export const ReviewsSectionHeader = composer()
  .withExperiments()
  .withMarketplaceStore()
  .withTranslation()
  .compose(
    ({
      editMode,
      marketplaceStore,
      afterPostRender,
      reviews,
      sortBy,
      showDeletedLink,
      isEligibleForReviews,
      appId,
    }) => {
      const { isMobile } = useConfigContext();
      const openModal = (app, eligibleForReviews) => {
        if (eligibleForReviews === false) {
          if (!isHelperOpen) {
            biLogger.report(
              appMarketReviewAppNotInstalledAlert({
                app_id: app?.baseInfo.id,
              }),
            );
          }
          if (!isMobile) {
            setOpenHelper(!isHelperOpen);
          } else {
            marketplaceStore.openModal(ModalNames.BLOCK_ADD_REVIEW_MOBILE, {
              app,
              closeModal: () => marketplaceStore.closeAllModals(),
            });
          }
          return;
        }
        editMode && editMode.shouldDisplay
          ? openEditModal(app)
          : openPostModal(app);
      };

      const openPostModal = (app: WebSolution) => {
        marketplaceStore.openModal(ModalNames.RATE_APP, {
          app,
          closeModal: () => closeReviewModal(app),
          optionCB: afterPostRender,
        });
      };

      const closeReviewModal = (app: WebSolution) => {
        marketplaceStore.closeAllModals();
      };

      const openEditModal = (app: WebSolution) => {
        marketplaceStore.openModal(ModalNames.EDIT_REVIEW, {
          app,
          closeModal: () => closeReviewModal(app),
          optionCB: afterPostRender,
          userReview: editMode.userReview,
        });
      };

      const [isHelperOpen, setOpenHelper] = React.useState(false);

      useEffect(() => {
        if (isHelperOpen) {
          setOpenHelper(false);
        }
      }, [appId]);
      return (
        <div className={s.reviewsHeaderContainer}>
          <div className={s.sortByContainer}>
            <ReviewsTitle reviews={reviews} />
            <SortDropDown sortBy={sortBy} isMobile={isMobile} />
          </div>
          {config.origin === Origin.STANDALONE &&
          !marketplaceStore.userName ? null : showDeletedLink ? (
            <ReviewDeleted />
          ) : (
            <Text className={s.openRateModalLink}>
              {editMode && !editMode.shouldDisplay && (
                <TranslatedKey tkey="solution.page.reviews.triedThisApp" />
              )}
              <SolutionPageCtx.Consumer>
                {(value) => (
                  <TextButton
                    dataHook="add-review"
                    onClick={() => openModal(value?.app, isEligibleForReviews)}
                  >
                    {editMode && editMode.shouldDisplay ? (
                      <TranslatedKey tkey="solution.page.reviews.editYourReview" />
                    ) : (
                      <BlockReviewsHelper
                        app={value?.app}
                        opened={isHelperOpen}
                        target="solution.page.reviews.shareYourFeedBack"
                      />
                    )}
                  </TextButton>
                )}
              </SolutionPageCtx.Consumer>
            </Text>
          )}
        </div>
      );
    },
  );

const ReviewDeleted = composer()
  .withTranslation()
  .compose((props) => {
    return (
      <Text className={s.openRateModalLink}>
        {props.t('solution.page.review.deletedText')}
        <br />
        {props.t('solution.page.review.please')}
        <TextButton
          as="a"
          target="_blank"
          href="https://www.wix.com/support/html5/wix-app-market/adding-apps-to-your-site/kb/why-was-my-app-review-removed"
        >
          {props.t('solution.page.review.deletedLink')}
        </TextButton>
      </Text>
    );
  });

const TranslatedKey = composer()
  .withTranslation()
  .compose(({ t, tkey }: { t?: TFunction; tkey: string }) => t(tkey));

const ReviewsTitle = composer()
  .withTranslation()
  .compose(({ t, reviews }: { t?: TFunction; reviews: number }) => {
    return (
      <Text
        size="medium"
        weight="normal"
        dataHook="solution-reviews-title"
        className={s.reviewTitle}
      >
        {t('solution.page.reviewsHeaderTitle', {
          reviews,
        })}
      </Text>
    );
  });
